import React, { useEffect, useRef, useState } from 'react'
import { SpeakingResultsScreen } from '../features/speaking/SpeakingResultsScreen.js'
import { SpeakingScreen } from '../features/speaking/SpeakingScreen.js'
import { t } from '../i18n/messages.js'
import { HomeScreen } from '../screens/HomeScreen.js'
import { LeaderboardScreen } from '../screens/LeaderboardScreen.js'
import { ProfileScreen } from '../screens/ProfileScreen.js'
import { ResultsTabScreen } from '../screens/ResultsTabScreen.js'
import { useAuthStore } from '../shared/auth-store.js'
import { usePreferencesStore } from '../shared/preferences-store.js'
import {
  type BottomTab,
  type Screen,
  useRouter,
} from '../shared/router-store.js'
import { applyAppTheme, tg, watchAppTheme } from '../shared/telegram.js'
import { BottomNav, ErrorState, LoadingDots } from '../shared/ui/index.js'

interface EBState {
  error: Error | null
}
class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  EBState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error: Error): EBState {
    return { error }
  }
  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info)
  }
  override render() {
    if (this.state.error) {
      return (
        <Centered>
          <ErrorState
            title="Something went wrong"
            message={this.state.error.message}
            primaryAction={{
              label: 'Reload',
              onClick: () => window.location.reload(),
            }}
          />
        </Centered>
      )
    }
    return this.props.children
  }
}

export function App() {
  const status = useAuthStore((s) => s.status)
  const error = useAuthStore((s) => s.error)
  const bootstrap = useAuthStore((s) => s.bootstrap)
  const screen = useRouter((s) => s.screen)
  const navigateToTab = useRouter((s) => s.navigateToTab)
  const themePreference = usePreferencesStore((s) => s.themePreference)
  const locale = usePreferencesStore((s) => s.locale)

  const [displayScreen, setDisplayScreen] = useState(screen)
  const [isExiting, setIsExiting] = useState(false)
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    tg?.ready()
    tg?.expand()
    void bootstrap()
  }, [bootstrap])

  useEffect(() => {
    applyAppTheme(themePreference)
  }, [themePreference])

  useEffect(() => {
    const unsubscribeTheme = watchAppTheme(() => {
      applyAppTheme(usePreferencesStore.getState().themePreference)
    })
    return () => unsubscribeTheme()
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — only re-run when screen changes, displayScreen is internal derived state
  useEffect(() => {
    if (screen === displayScreen) return
    setIsExiting(true)
    exitTimerRef.current = setTimeout(() => {
      setDisplayScreen(screen)
      setIsExiting(false)
    }, 160)
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current)
    }
  }, [screen])

  if (status === 'loading' || status === 'idle') {
    return (
      <Centered>
        <LoadingDots label={t(locale, 'app.loading')} />
      </Centered>
    )
  }

  if (status === 'error') {
    return (
      <Centered>
        <ErrorState
          title={t(locale, 'app.signInError')}
          message={error ?? undefined}
          primaryAction={{
            label: t(locale, 'app.tryAgain'),
            onClick: () => void bootstrap(),
          }}
        />
      </Centered>
    )
  }

  const hideBottomNav =
    displayScreen.name === 'speaking' ||
    displayScreen.name === 'speaking_results'

  const activeTab: BottomTab =
    displayScreen.name === 'speaking' ||
    displayScreen.name === 'speaking_results'
      ? 'home'
      : displayScreen.name

  return (
    <div
      className={`h-full flex flex-col${isExiting ? ' animate-fade-out pointer-events-none' : ''}`}
    >
      <div
        className={
          hideBottomNav
            ? 'flex-1 min-h-0 overflow-y-auto'
            : 'flex-1 min-h-0 overflow-y-auto pb-[max(6.75rem,calc(5.25rem+env(safe-area-inset-bottom,0px)))]'
        }
      >
        {renderScreen(displayScreen)}
      </div>
      {!hideBottomNav && (
        <BottomNav
          active={activeTab}
          locale={locale}
          onSelect={(tab) => navigateToTab(tab)}
        />
      )}
    </div>
  )
}

function renderScreen(screen: Screen) {
  switch (screen.name) {
    case 'home':
      return (
        <AppErrorBoundary>
          <HomeScreen />
        </AppErrorBoundary>
      )
    case 'results':
      return (
        <AppErrorBoundary>
          <ResultsTabScreen />
        </AppErrorBoundary>
      )
    case 'leaderboard':
      return (
        <AppErrorBoundary>
          <LeaderboardScreen />
        </AppErrorBoundary>
      )
    case 'profile':
      return (
        <AppErrorBoundary>
          <ProfileScreen />
        </AppErrorBoundary>
      )
    case 'speaking':
      return (
        <AppErrorBoundary>
          <SpeakingScreen mode={screen.mode} part={screen.part} />
        </AppErrorBoundary>
      )
    case 'speaking_results':
      return (
        <AppErrorBoundary>
          <SpeakingResultsScreen sessionId={screen.sessionId} />
        </AppErrorBoundary>
      )
  }
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-full flex items-center justify-center p-6 text-brand-text">
      {children}
    </div>
  )
}
